import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import Link from 'react-router-dom/Link'
import Popup from 'reactjs-popup'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import OrganizationAvatar from 'srcReact/components/common/OrganizationAvatar'
import SearchTextBox from 'srcReact/components/common2/SearchTextBox'
import Icon from 'srcReact/components/icons/Icon'
import IconButton from 'srcReact/components/common2/IconButton'
import OrganizationSideBar from 'srcReact/components/navigation/OrganizationSideBar'
import GlobalSearchPopin from 'srcReact/components/popinsContent/GlobalSearchPopin'
import GlobalSearchSuggestionPopin from 'srcReact/components/popinsContent/GlobalSearchSuggestionPopin'
import { redirectToPage } from 'srcReact/helpers/history'
import { hasNotifications } from 'srcReact/helpers/organizationHelper'
import { canCreateGroup } from 'srcReact/helpers/userHelper'
import { getOrganizationAccronym } from 'srcReact/helpers/utils'
import { organizationActions } from 'srcReact/OrganizationPage'
import NotificationList from 'srcReact/OrganizationPage/NotificationList'
import { globalSearchActions } from 'srcReact/SearchPage'
import MainMenu from '../MainMenu'
import QuickCreateMenu from '../QuickCreateMenu'
import styles from './styles.module.scss'

function DesktopHeader({
  currentOrganization,
  loggedInUser,
  orgaSideBarOpened,
  setOrgaSideBarOpened,
  organizationList,
  isNative,
  messenger,
  notifications
}) {
  const { t } = useTranslation()

  const dispatch = useDispatch()
  const {
    globalSearch: { searchText, searchSelectedMember, searchSuggestionList },
    organization: {
      header: { user_properties }
    }
  } = useSelector((state) => state)
  const [showSearchPopup, setShowSearchPopup] = useState(false)
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false)
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const [tabPressed, setTabPressed] = useState(false)

  useEffect(() => {
    const el = document.getElementById('globalSearch')
    if (el === document.activeElement && searchText.length >= appConstantSettings.GLOBAL_SEARCH_LENGTH) {
      setShowSearchPopup(true)
      setIsSearchFocused(true)
    }
  }, [searchText])
  useEffect(() => {
    if (searchText.length === 0) {
      setShowSearchPopup(false)
      setIsSearchFocused(false)
    }
  }, [searchText])

  const handleSearch = (value) => {
    dispatch(globalSearchActions.setSearchText(value))
  }
  const handleSearchFocus = () => {
    setShowSearchPopup(true)
    setIsSearchFocused(true)
  }
  const handleSearchBlur = (e) => {
    if (searchSelectedMember) {
      return setShowSearchPopup(true)
    }

    //do not close search popup on blur
    if (!tabPressed) {
      clearSearch()
    } else {
      setTabPressed(false)
    }
  }
  const handleSearchKeyDown = (key) => {
    if (key === 'Enter') {
      setIsSearchFocused(false)
      setShowSearchPopup(false)
      redirectToPage('/0/search')
    } else if (key === 'Escape') {
      clearSearch()
    } else if (key === 'Tab') {
      setTabPressed(true)
    }
  }

  const clearSearch = () => {
    setIsSearchFocused(false)
    setShowSearchPopup(false)
    handleSearch('')
  }

  const renderActiveOrgName = () => {
    return (
      <span className={styles.activeOrg}>
        {currentOrganization.hasOwnProperty('info') && (
          <img
            src={currentOrganization.info?.picture}
            height='48'
            className={styles.orgLogo}
            alt={currentOrganization.info.name}
          />
        )}
      </span>
    )
  }

  return (
    <header
      className={`${styles.mainHeader} ${currentOrganization && styles.activeOrg}`}
      aria-label={t('formlabel.mainHeader')}>
      {currentOrganization ? (
        <React.Fragment>
          <div className={styles.orgAvatarContainer}>
            <OrganizationAvatar
              color={currentOrganization.info.color || appConstantSettings.DEFAULT_COLOR}
              hideSubTitle={true}
              subTitle={currentOrganization?.info?.name}
              onClick={() => {
                return setOrgaSideBarOpened(!orgaSideBarOpened)
              }}
              title={currentOrganization.info.accronym || getOrganizationAccronym(currentOrganization.info.name)}
              fullOrgName={currentOrganization?.info?.name}
              notificationNumber={notifications?.unread_count}
            />
            <IconButton
              iconName='chevron-down'
              iconSize='medium'
              aria-label={t('formlabel.showMoreOrgs')}
              onClick={() => {
                return setOrgaSideBarOpened(!orgaSideBarOpened)
              }}
            />
            {orgaSideBarOpened && (
              <OrganizationSideBar
                currentOrganization={currentOrganization.info}
                organizations={organizationList.items}
                organizationAction={(orga, redirect) => {
                  dispatch(organizationActions.switchOrganization(orga, redirect))
                }}
                close={() => setOrgaSideBarOpened(false)}
                user_properties={user_properties}
              />
            )}
          </div>
          {isNative ? (
            <a href={`/0/${appConstantSettings.DEFAULT_HOMEPAGE}`} className={styles.activeOrgWrapper}>
              {renderActiveOrgName()}
            </a>
          ) : (
            <Link
              to={`/0/${appConstantSettings.DEFAULT_HOMEPAGE}`}
              className={styles.activeOrgWrapper}
              aria-label={`${t('formlabel.organization')} ${currentOrganization.info.name}`}>
              {renderActiveOrgName()}
            </Link>
          )}
        </React.Fragment>
      ) : (
        <a
          href='/'
          className={styles.activeOrgWrapper}
          aria-label={`${t('formlabel.organization')} ${currentOrganization?.info?.name}`}>
          <img alt='' className={styles.activeOrgWrapperMixr} src={`${config.assetUrl}/images/logo-mixr.png`} />
        </a>
      )}
      {loggedInUser && (
        <nav className={styles.nav}>
          <ul id='main_menu' className={styles.navList} aria-label={t('formlabel.headerNavigation')}>
            <li className={`${styles.navItem} ${styles.search}`}>
              <SearchTextBox
                id='globalSearch'
                value={searchText}
                {...(searchText.length === 0 && { clear: true })}
                minimumSearchChar={1}
                size='large'
                iconPosition='start'
                placeholder={isSearchFocused ? t('globalSearch.searchPlaceholder') : t('global.search')}
                handleSearch={handleSearch}
                cssClass={`${styles.searchInputContainer} ${isSearchFocused ? styles.focused : ''}`}
                iconCSS={`${styles.iconCSS} ${isSearchFocused ? styles.focused : ''}`}
                inputCssClass={styles.searchInput}
                buttonCssClass={`${styles.buttonCssClass} ${isSearchFocused ? styles.focused : ''}`}
                handleFocus={handleSearchFocus}
                handleBlur={handleSearchBlur}
                handleKeyDown={handleSearchKeyDown}
                style='secondary'
              />
              {showSearchPopup &&
                searchText.length < appConstantSettings.GLOBAL_SEARCH_LENGTH &&
                searchSuggestionList.length >= 1 && (
                  <GlobalSearchSuggestionPopin isHeader={true} handleSearch={handleSearch} closeModal={clearSearch} />
                )}
              {showSearchPopup && searchText.length >= appConstantSettings.GLOBAL_SEARCH_LENGTH && (
                <GlobalSearchPopin isHeader={true} closeModal={clearSearch} />
              )}
            </li>

            {currentOrganization && canCreateGroup(currentOrganization) && (
              <li className={styles.navItem}>
                <QuickCreateMenu isNative={isNative} />
              </li>
            )}

            <li className={styles.navItem}>
              <button
                id='notification_icon_button'
                className={`${styles.iconBadge} ${styles.iconNotification} ${
                  isNotificationModalOpen && styles.iconNotificationOpen
                }`}
                onClick={() => setIsNotificationModalOpen(true)}
                aria-label={`${t('formlabel.openNotifications')} ${
                  notifications?.unread_count > 0
                    ? t('formlabel.notificationCount', { count: notifications?.unread_count })
                    : ''
                }`}>
                <Icon name='bell' size='large24' />
                {/* {notifications && notifications.new_notification && <span className={styles.iconBadgeCount} />} */}
                {notifications && notifications.unread_count > 0 && notifications.new_notification && (
                  <span className={styles.iconBadgeCount}>
                    {notifications.unread_count > 99 ? '99+' : notifications.unread_count}
                  </span>
                )}
              </button>
            </li>
            <li className={styles.navItem}>
              <MainMenu
                currentOrganization={currentOrganization}
                user={loggedInUser}
                isNative={isNative}
                user_properties={user_properties}
              />
            </li>
            <li className={styles.navItem}>
              <button
                id='onboarding_messenger'
                className={`${styles.iconBadge} ${styles.iconMessage} ${
                  messenger && messenger.unread_count && styles.unreadCount
                }`}
                onClick={() => redirectToPage(`/0/messenger`)}
                aria-label={`${t('formlabel.gotoMessenger')} ${
                  messenger?.unread_count > 0
                    ? t('formlabel.messengerNotificationCount', { count: messenger?.unread_count })
                    : ''
                }`}>
                <Icon name='chat24' size='large32' />
                {messenger && messenger.unread_count > 0 && (
                  <span className={styles.iconBadgeCount}>
                    {messenger.unread_count > 99 ? '99+' : messenger.unread_count}
                  </span>
                )}
                <Icon name='arrow-right' size='large24' />
              </button>
            </li>
          </ul>
        </nav>
      )}
      {isNotificationModalOpen && (
        <NotificationList isOpen={isNotificationModalOpen} closeModal={() => setIsNotificationModalOpen(false)} />
      )}
    </header>
  )
}

export default DesktopHeader
